import React, { useState } from 'react';
import {
  downloadOnboardingArchiveZip,
  postOnboardingFinalize,
  type OnboardingFinalizeResponse,
} from '../../../../services/onboardingFinalizeApi';
import { safeFileNamePart } from './utils';
import './FinalizationModal.css';

export interface FinalizationModalProps {
  employeeName: string;
  employeeEmail: string;
  invitationId: string;
  onClose: () => void;
  /** Called after finalize completes (with or without zip). */
  onComplete: () => void;
}

const FinalizationModal: React.FC<FinalizationModalProps> = ({
  employeeName,
  employeeEmail,
  invitationId,
  onClose,
  onComplete,
}) => {
  const [phase, setPhase] = useState<'confirm' | 'running' | 'done'>('confirm');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardingFinalizeResponse | null>(null);

  const runFinalize = async (downloadZip: boolean) => {
    setPhase('running');
    setError(null);
    try {
      const res = await postOnboardingFinalize(employeeEmail, invitationId);
      setResult(res);
      if (downloadZip) {
        const name = safeFileNamePart(employeeName || employeeEmail) + '_書類一式.zip';
        await downloadOnboardingArchiveZip(employeeEmail, invitationId, name);
      }
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : '登録完了処理に失敗しました。');
      setPhase('confirm');
    }
  };

  return (
    <div className="finalization-modal" role="dialog" aria-modal="true" aria-labelledby="finalization-modal-title">
      <div className="finalization-modal__backdrop" onClick={phase === 'confirm' ? onClose : undefined} aria-hidden />
      <div className="finalization-modal__panel">
        <header className="finalization-modal__header">
          <h2 id="finalization-modal-title" className="finalization-modal__title">
            登録完了処理
          </h2>
          {phase === 'confirm' && (
            <button type="button" className="finalization-modal__close" onClick={onClose} aria-label="閉じる">
              ×
            </button>
          )}
        </header>

        <div className="finalization-modal__body">
          {phase === 'confirm' && (
            <>
              <p>
                <strong>{employeeName || employeeEmail}</strong> 様の書類はすべて承認され、承認メールを送信しました。
              </p>
              <p>登録完了処理では次のことを行います。</p>
              <ul className="finalization-modal__list">
                <li>承認済みのアップロード書類と記入書類 PDF をアーカイブフォルダに保存</li>
                <li>却下された提出ラウンドのファイル・フォームデータ・スナップショットを削除</li>
              </ul>
              <p className="finalization-modal__note">
                ZIPをダウンロードするか、ダウンロードせずに完了できます。いずれの場合もアーカイブはサーバーに保存されます。
              </p>
              <p className="finalization-modal__defer-warning">
                「あとで」を選ぶと承認は完了しますが、却下データの削除とアーカイブ作成は行われません。後から提出確認または従業員の「登録完了処理」で実行できます。
              </p>
              {error && (
                <p className="finalization-modal__error" role="alert">
                  {error}
                </p>
              )}
            </>
          )}

          {phase === 'running' && (
            <p className="finalization-modal__running">登録完了処理を実行しています…</p>
          )}

          {phase === 'done' && result && (
            <div className="finalization-modal__result">
              <p>{result.message}</p>
              <ul className="finalization-modal__list">
                <li>アップロード書類: {result.archivedVariousCopyCount ?? 0} 件</li>
                <li>記入書類 PDF: {result.archivedFormPdfCount ?? 0} 件</li>
                <li>削除した却下ファイル: {result.deletedVariousCopyFileCount ?? 0} 件</li>
                <li>削除した却下フォームラウンド: {result.deletedFormRoundCount ?? 0} 件</li>
                <li>削除したスナップショット: {result.deletedSnapshotCount ?? 0} 件</li>
              </ul>
              {result.archivePath && (
                <p className="finalization-modal__path">保存先: {result.archivePath}</p>
              )}
            </div>
          )}
        </div>

        <footer className="finalization-modal__footer">
          {phase === 'confirm' && (
            <>
              <button
                type="button"
                className="finalization-modal__btn finalization-modal__btn--secondary"
                onClick={onClose}
                title="アーカイブ未作成のまま閉じます"
              >
                あとで
              </button>
              <button
                type="button"
                className="finalization-modal__btn finalization-modal__btn--secondary"
                onClick={() => void runFinalize(false)}
              >
                ダウンロードせず完了
              </button>
              <button
                type="button"
                className="finalization-modal__btn finalization-modal__btn--primary"
                onClick={() => void runFinalize(true)}
              >
                ZIPをダウンロードして完了
              </button>
            </>
          )}
          {phase === 'done' && (
            <button
              type="button"
              className="finalization-modal__btn finalization-modal__btn--primary"
              onClick={() => {
                onComplete();
              }}
            >
              閉じる
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default FinalizationModal;
